import { times, flatten } from 'lodash';
import { useMemo } from 'react';

import Card from 'components/Card';
import ClientTeamMemberSelect from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import { guestRequired } from 'utilities/validators';

import { RoomRendererProps } from './types';

const RoomRenderer = ({
  roomType,
  roomConfigurations,
  fallbackRoomCount,
}: RoomRendererProps) => {
  const roomComponents = useMemo(() => {
    const configs = roomConfigurations?.[roomType];

    if (configs) {
      return flatten(
        configs.map((config, configIndex) =>
          times(config.rooms, (roomIndex) => {
            const globalIndex =
              configs
                .slice(0, configIndex)
                .reduce((acc, c) => acc + c.rooms, 0) + roomIndex;

            const isDoubleWithTwoGuests =
              roomType === 'double' && config.guests === 2;

            return (
              <div
                key={`${roomType}-${globalIndex}`}
                className=""
                {...(roomType === 'suite'
                  ? { 'data-testid': 'suite-room-selections' }
                  : {})}
              >
                <Card className="overflow-visible bg-gray-50">
                  <h3 className="mb-2 font-oswald text-base font-semibold text-gray-900">
                    Room {roomType} ({config.guests} guest
                    {config.guests > 1 ? 's' : ''})
                  </h3>
                  <div className="flex w-full flex-col gap-2">
                    {isDoubleWithTwoGuests ? (
                      // Special double room layout for 2 guests
                      <>
                        <div className="w-full">
                          <label
                            htmlFor={`${roomType}[${globalIndex}][firstBed]`}
                            className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            First Bed
                          </label>
                          <ClientTeamMemberSelect
                            name={`${roomType}[${globalIndex}][firstBed]`}
                            validate={guestRequired}
                            testId={`${roomType}-room-select-${globalIndex}-first-bed`}
                            allowCreateGuest={true}
                          />
                        </div>
                        <div className="w-full">
                          <label
                            htmlFor={`${roomType}[${globalIndex}][secondBed]`}
                            className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            Second Bed{' '}
                            <span className="text-xs text-gray-500">
                              (Optional)
                            </span>
                          </label>
                          <ClientTeamMemberSelect
                            name={`${roomType}[${globalIndex}][secondBed]`}
                            testId={`${roomType}-room-select-${globalIndex}-second-bed`}
                            allowCreateGuest={true}
                          />
                        </div>
                      </>
                    ) : config.guests === 1 ? (
                      <div className="w-full">
                        <ClientTeamMemberSelect
                          name={`${roomType}[${globalIndex}]`}
                          validate={guestRequired}
                          testId={`${roomType}-room-select-${globalIndex}`}
                          allowCreateGuest={true}
                        />
                      </div>
                    ) : (
                      times(config.guests, (guestIndex) => (
                        <div key={`guest-${guestIndex}`} className="w-full">
                          <ClientTeamMemberSelect
                            name={`${roomType}[${globalIndex}][guest${guestIndex}]`}
                            validate={guestRequired}
                            testId={`${roomType}-room-select-${globalIndex}-guest-${guestIndex}`}
                            allowCreateGuest={true}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </div>
            );
          }),
        ),
      );
    }

    return times(fallbackRoomCount, (index) => (
      <div
        key={`${roomType}-${index}`}
        className=""
        {...(roomType === 'suite'
          ? { 'data-testid': 'suite-room-selections' }
          : {})}
      >
        <Card className="overflow-visible bg-gray-50">
          <h3 className="mb-2 font-oswald text-base font-semibold text-gray-900">
            Room {roomType}
          </h3>
          <div
            className={`flex w-full flex-col gap-2 ${roomType === 'suite' ? 'lg:flex-row' : ''}`}
          >
            {roomType === 'double' ? (
              // Default double room layout (2 guests)
              <>
                <div className="w-full">
                  <ClientTeamMemberSelect
                    name={`${roomType}[${index}][firstBed]`}
                    validate={guestRequired}
                    testId={`${roomType}-room-select-${index}-first-bed`}
                    allowCreateGuest={true}
                  />
                </div>
                <div className="w-full">
                  <ClientTeamMemberSelect
                    name={`${roomType}[${index}][secondBed]`}
                    testId={`${roomType}-room-select-${index}-second-bed`}
                    validate={guestRequired}
                    allowCreateGuest={true}
                  />
                </div>
              </>
            ) : (
              // Single guest room (single/suite)
              <div className="w-full">
                <ClientTeamMemberSelect
                  name={`${roomType}[${index}]`}
                  validate={guestRequired}
                  testId={`${roomType}-room-select-${index}`}
                  allowCreateGuest={true}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    ));
  }, [roomConfigurations, roomType, fallbackRoomCount]);

  return <>{roomComponents}</>;
};

export default RoomRenderer;
